/**
 * PlaceReadinessCard
 * ---------------------------------------------------------------
 * Progress readout for the Place pillar — mirrors ApplyReadinessCard
 * so the "How do I move on?" question is answered the moment a
 * student shortlists their first city.
 *
 * Reads the shared useCityShortlist store; renders a compact
 * horizontal pill with:
 *   • A running "N" counter (purple → green when the threshold is met)
 *   • Copy: "You have added N of the minimum 1 city needed to move
 *           to the Prove stage."
 *   • A 4-segment tube-map-style progress bar
 *   • A "Confirm & go to Prove →" CTA once ready, deep-linking to the
 *     matcher confirm panel where the pillar-level Confirm lives.
 *
 * PLACE_MIN_CITIES is exported so the matcher/compare pages can
 * source the same minimum — one place to change, no drift.
 */
import { Link } from "react-router-dom";
import { useCityShortlist } from "@/hooks/useCityShortlist";

export const PLACE_MIN_CITIES = 1;
const BAR_SEGMENTS = 4; // visual only — cap-aligned

export default function PlaceReadinessCard({
  compact = false,
  className = "",
  testId = "place-readiness-card",
  hideWhenEmpty = false,
}) {
  const { slugs } = useCityShortlist();
  const have = slugs.length;

  if (hideWhenEmpty && have === 0) return null;

  const need = PLACE_MIN_CITIES;
  const ready = have >= need;

  return (
    <div
      data-testid={testId}
      className={[
        "rounded-2xl border flex flex-wrap items-center gap-3",
        compact ? "px-3 py-2" : "px-4 py-3",
        ready
          ? "border-emerald-200 bg-emerald-50"
          : "border-stone-200 bg-white",
        className,
      ].join(" ")}
    >
      <div className="flex items-center gap-2 shrink-0">
        <span
          aria-hidden="true"
          className={[
            "inline-flex items-center justify-center rounded-full font-mono font-semibold",
            compact ? "h-6 w-6 text-xs" : "h-8 w-8 text-sm",
            ready
              ? "bg-emerald-600 text-white"
              : have > 0
                ? "bg-[#4C1D95] text-white"
                : "bg-stone-200 text-stone-700",
          ].join(" ")}
        >
          {have}
        </span>
        <span className={[
          "uppercase tracking-wider text-stone-500 font-medium",
          compact ? "text-[10px]" : "text-[11px]",
        ].join(" ")}>
          of {need} to unlock Prove
        </span>
      </div>
      <p className={[
        "text-stone-700 flex-1 min-w-0",
        compact ? "text-xs" : "text-sm",
      ].join(" ")}>
        {ready
          ? <>You have shortlisted <strong>{have}</strong> {have === 1 ? "city" : "cities"} — head to <strong>Prove</strong> next, or compare a few side-by-side first.</>
          : <>You have added <strong>{have}</strong> of the minimum <strong>{need}</strong> {need === 1 ? "city" : "cities"} needed to move to the <strong>Prove</strong> stage.</>}
      </p>
      <div className="flex items-center gap-1" aria-hidden="true">
        {Array.from({ length: BAR_SEGMENTS }).map((_, i) => (
          <span
            key={i}
            className={[
              "h-1.5 rounded-full transition-colors",
              compact ? "w-5" : "w-6",
              i < have ? "bg-emerald-500" : "bg-stone-200",
            ].join(" ")}
          />
        ))}
      </div>
      {ready && (
        <Link
          to="/cities/matcher#confirm-place"
          data-testid={`${testId}-cta`}
          className={[
            "inline-flex items-center gap-1 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-medium",
            compact ? "text-[11px] px-2.5 py-1" : "text-xs px-3 py-1.5",
          ].join(" ")}
        >
          Confirm &amp; go to Prove <span aria-hidden="true">→</span>
        </Link>
      )}
    </div>
  );
}
